/*
  ba-coll-live-filter.js
  WHAT: Applies collection facets in the browser - no page reload, OR within a group, AND across
        groups, and options that would return nothing are disabled.
  WHY:  Owner 2026-09-30: "and when you tick it it should not reload the page", "it should be
        like does [Bellroy]", then a screenshot of Bellroy with Backpack, Duffel AND Bucket Bag
        ticked together, and "Matte Polycarbonate" greyed out.
  WHEN: Added 2026-09-30.

  WHY OR IS ONLY POSSIBLE HERE. A Shopify tag path ANDs its terms:
  /collections/speakers/f_splashproof+f_waterproof asks for a product that is BOTH and returns
  nothing. That is why the server-rendered chips behaved as single-select, and why the owner's
  "splashproof and waterproof are mutually exclusive" was true of the old mechanism. Filtering in
  the browser lifts that restriction: ticking both now means "show me either", which is what a
  shopper means and what Bellroy does.

  STATE LIVES IN ?filter.p.tag=, NOT IN THE PATH. A comma-separated query string can express a
  set the tag path cannot, and it survives a reload: Shopify ignores the parameter server-side
  and sends the whole collection, then this file reproduces the selection. So a filtered URL is
  still shareable and still correct.

  Build rule 28 applies and is satisfied: storefront filtering is NOT configured on this shop, so
  client-side filtering is required rather than optional, and the grid must fail visible-with-all
  rather than blank. The chips remain real links, so with JS off every one of them still works.
*/

(function () {
  'use strict';

  var grid = document.querySelector('.ba-coll__grid');
  var bar = document.querySelector('.ba-coll__chip-bar');
  if (!grid || !bar) return;

  /* CLAIM THE GRID. `ba-chip-active.js` carries filterCollectionGrid(), the 2026-06-11
     implementation this file superseded, and it still runs site-wide from site-header.liquid.
     Both write style.display on the same .ba-ptile-wrap elements, but they disagree about the
     format of `filter.p.tag`: this file splits it on commas for multi-select, that one treats
     the whole string as ONE literal tag.

     On a multi-tag URL - which is exactly what multi-select produces - the old one normalises
     "f_led,f_mic_wired" to a single tag, matches nothing, and hides EVERY product. Script order
     hides this on first paint because we run second and win, but ba-chip-active.js also binds
     `pageshow`, so a back-navigation or bfcache restore re-runs it and empties the grid with
     nothing to re-run ours.

     This flag lets the older implementation stand down wherever this one is active, which is
     every collection page. It is set before any early return below so the claim holds even on
     the paths where this file stops early. */
  window.__baLiveFilter = true;

  var base = (location.pathname.match(/^(\/collections\/[^\/]+)/) || [])[1];
  if (!base) return;

  function selected() {
    var raw = new URLSearchParams(location.search).get('filter.p.tag');
    return raw ? raw.split(',').filter(Boolean) : [];
  }

  // Tags carried in a server-filtered path, e.g. /collections/speakers/ia_nav_party+f_led
  function pathTags() {
    if (location.pathname === base) return [];
    return decodeURIComponent(location.pathname.slice(base.length + 1)).split('+').filter(Boolean);
  }

  /* Price band, held in Shopify's own parameter names so the URL stays the one the server
     understands: ?filter.v.price.gte=50&filter.v.price.lte=99.

     Owner 2026-09-30 pointed at /collections/earbuds?filter.v.price.lte=49 — the price group was
     the last control still costing a round trip, because I had excluded it on the grounds that a
     price filter reads the live variant price and the DOM could not recompute it. It can:
     product-tile.liquid already emits data-price on every tile. Units differ and that matters —
     data-price is CENTS (29900), the band bounds are DOLLARS (50, 99) — so one side is divided
     before comparing. */
  function priceRange() {
    var q = new URLSearchParams(location.search);
    var gte = q.get('filter.v.price.gte');
    var lte = q.get('filter.v.price.lte');
    return {
      min: gte === null || gte === '' ? null : parseFloat(gte),
      max: lte === null || lte === '' ? null : parseFloat(lte)
    };
  }

  function priceOk(wrap, range) {
    if (range.min === null && range.max === null) return true;
    var el = wrap.querySelector('[data-price]') || wrap;
    var cents = parseInt(el.getAttribute('data-price'), 10);
    if (isNaN(cents)) return true; // never hide a product over missing data
    var dollars = cents / 100;
    if (range.min !== null && dollars < range.min) return false;
    if (range.max !== null && dollars > range.max) return false;
    return true;
  }

  function groupOf(chip) {
    return chip.closest('.ba-coll__filter-group-chips');
  }

  function tagsIn(group) {
    return Array.prototype.map.call(group.querySelectorAll('[data-filter-tag]'), function (c) {
      return c.getAttribute('data-filter-tag');
    });
  }

  function tileTags(wrap) {
    var el = wrap.querySelector('[data-tags]') || wrap;
    return (el.getAttribute('data-tags') || '').toLowerCase().split(',');
  }

  function wraps() {
    return grid.querySelectorAll('.ba-ptile-wrap');
  }

  /* INDEX THE DOM ONCE PER PASS, NOT ONCE PER COMPARISON.

     Measured 2026-10-02 on /collections/earbuds at 375px: a single chip tap cost 82-311ms of
     SYNCHRONOUS handler time, and a CPU profile of that window attributed 304ms to
     querySelectorAll and 197ms to querySelector. The cause was structural, not incidental -
     matches() re-queried every filter group on every call, tileTags() re-queried the tile, and
     wraps() re-queried the grid, while disableDeadEnds() called all three inside nested loops
     over 29 chips x 8 tiles. That is roughly 2,580 DOM queries per tap on a SMALL grid; the
     cost is linear in tile count, so Deals at 89 products is about ten times worse.

     Nothing inside those loops changes between iterations, so it is all cacheable. The chips
     and the tiles are read once into plain arrays and Sets, and the matching becomes
     memory work. Real-user p75 INP on this store is 240ms against a 200ms bar, and this was
     the single worst interaction in the theme.

     The cache MUST be invalidated whenever the grid contents or the chip set change - see
     invalidateIndex() below and its call site after the grid swap. A stale index would filter
     against products that are no longer on the page, which is a correctness bug, not a
     performance one. */
  var _groupTags = null;   // [[tag, ...], ...] one entry per filter group
  var _tiles = null;       // [{ el, tags: Set }, ...]

  function groupTags() {
    if (!_groupTags) {
      _groupTags = Array.prototype.map.call(
        bar.querySelectorAll('.ba-coll__filter-group-chips'), tagsIn);
    }
    return _groupTags;
  }

  function tiles() {
    if (!_tiles) {
      _tiles = Array.prototype.map.call(wraps(), function (w) {
        var set = Object.create(null);
        tileTags(w).forEach(function (t) { set[t] = true; });
        return { el: w, tags: set };
      });
    }
    return _tiles;
  }

  function invalidateIndex() {
    _groupTags = null;
    _tiles = null;
  }

  // A tile survives when, for EVERY group that has a selection, it carries at least one of that
  // group's selected tags. Groups AND together; values inside a group OR together.
  // `have` is the tile's tag SET from the index - membership is a property lookup, not a scan.
  function matches(have, sel) {
    var groups = groupTags();
    for (var i = 0; i < groups.length; i++) {
      var mine = groups[i].filter(function (t) { return sel.indexOf(t) !== -1; });
      if (!mine.length) continue;
      var hit = mine.some(function (t) { return have[t.toLowerCase()] === true; });
      if (!hit) return false;
    }
    return true;
  }

  function render(sel) {
    var shown = 0;
    var range = priceRange();
    // .ba-ptile-wrap, not [data-tags]: product-tile.liquid emits data-tags twice per product -
    // on the wrap and again on the inner anchor. Hiding the inner one leaves an empty cell.
    tiles().forEach(function (t) {
      var ok = matches(t.tags, sel) && priceOk(t.el, range);
      t.el.style.display = ok ? '' : 'none';
      if (ok) shown++;
    });

    // Price chips carry their own active state — the band in the URL, not a tag.
    Array.prototype.forEach.call(bar.querySelectorAll('.ba-coll__chip--price'), function (chip) {
      var lo = chip.getAttribute('data-price-min');
      var hi = chip.getAttribute('data-price-max');
      var on;
      if (chip.classList.contains('ba-coll__chip--clear')) {
        on = range.min === null && range.max === null;
      } else {
        on = (lo ? parseFloat(lo) : null) === range.min && (hi ? parseFloat(hi) : null) === range.max;
      }
      chip.classList.toggle('is-active', on);
      if (on) chip.setAttribute('aria-current', 'true');
      else chip.removeAttribute('aria-current');
    });

    // Ticks, and open any group holding one so a selection is never hidden in a closed box.
    Array.prototype.forEach.call(bar.querySelectorAll('[data-filter-tag]'), function (chip) {
      var on = sel.indexOf(chip.getAttribute('data-filter-tag')) !== -1;
      chip.classList.toggle('is-active', on);
      if (on) {
        chip.setAttribute('aria-current', 'true');
        var g = chip.closest('details.ba-coll__filter-group');
        if (g) g.open = true;
      } else {
        chip.removeAttribute('aria-current');
      }
    });

    disableDeadEnds(sel);

    var count = document.querySelector('.ba-coll__result-count');
    if (count) count.innerHTML = '<strong>' + shown + '</strong> ' + (shown === 1 ? 'product' : 'products');

    // Visible whenever ANYTHING is applied — a price band counts, and only checking the tag
    // selection left "Clear all" hidden after a price-only filter, with no way back to the full
    // set except the All chip.
    var anyApplied = sel.length > 0 || range.min !== null || range.max !== null;
    var clear = document.querySelector('.ba-coll__clear-all');
    if (clear) clear.classList.toggle('is-visible', anyApplied);
  }

  // Grey out a value that would return nothing if added - Bellroy's "Matte Polycarbonate".
  // Computed by asking what the result WOULD be, never assumed.
  function disableDeadEndsNow(sel) {
    var all = tiles();
    Array.prototype.forEach.call(bar.querySelectorAll('[data-filter-tag]'), function (chip) {
      var tag = chip.getAttribute('data-filter-tag');
      if (sel.indexOf(tag) !== -1) {            // already on - removing it can only widen
        chip.classList.remove('is-disabled');
        return;
      }
      var probe = sel.concat([tag]);
      var any = all.some(function (t) { return matches(t.tags, probe); });
      chip.classList.toggle('is-disabled', !any);
    });
  }

  /* RUN THE GREYING OUT AFTER THE PAINT, NOT INSIDE THE TAP.

     INP measures from the interaction to the NEXT PAINT. Everything done synchronously in the
     click handler lands inside that measurement, and this is the most expensive part of the
     pass - it probes every unselected chip against every tile. The shopper does not need the
     greying in the same frame as the filtering; they need the grid to respond.

     So render() paints the filtered grid, and this runs a frame later. rAF fires before the
     paint, so the nested setTimeout hands the work to a separate task AFTER it. Any pending
     run is cancelled when a newer tap arrives, which also stops rapid tapping from queueing
     several full passes. */
  var _deadEndTimer = null;
  var _deadEndRaf = null;

  function disableDeadEnds(sel) {
    if (_deadEndRaf) cancelAnimationFrame(_deadEndRaf);
    if (_deadEndTimer) clearTimeout(_deadEndTimer);
    _deadEndRaf = requestAnimationFrame(function () {
      _deadEndRaf = null;
      _deadEndTimer = setTimeout(function () {
        _deadEndTimer = null;
        disableDeadEndsNow(sel);
      }, 0);
    });
  }

  function push(sel) {
    var qs = new URLSearchParams(location.search);
    if (sel.length) qs.set('filter.p.tag', sel.join(','));
    else qs.delete('filter.p.tag');
    var s = qs.toString();
    history.pushState({}, '', base + (s ? '?' + s : ''));
  }

  bar.addEventListener('click', function (e) {
    // "Clear all" and the "All" reset chip do the same job: drop every selection. Both are
    // handled here so neither falls through to a navigation — the All chip has no
    // data-filter-tag, so without this it reloaded while every other control did not.
    if (e.target.closest('.ba-coll__clear-all') || e.target.closest('.ba-coll__chip--all')) {
      e.preventDefault();
      // Clear EVERYTHING, price band included. push() only touches the tag parameter, so a
      // price filter would otherwise survive a control labelled "Clear all filters".
      var q0 = new URLSearchParams(location.search);
      q0.delete('filter.p.tag');
      q0.delete('filter.v.price.gte');
      q0.delete('filter.v.price.lte');
      var s0 = q0.toString();
      history.pushState({}, '', base + (s0 ? '?' + s0 : ''));
      render([]);
      return;
    }

    // Price bands. Owner 2026-09-30: "i click a different price and it then reloads the whole
    // page waste of data." Handled here rather than left to navigate — data-price is already on
    // every tile, so the DOM can answer this without a round trip.
    var price = e.target.closest('.ba-coll__chip--price');
    if (price) {
      e.preventDefault();
      var q = new URLSearchParams(location.search);
      var lo = price.getAttribute('data-price-min');
      var hi = price.getAttribute('data-price-max');
      // "Any price" clears the band and keeps every other selection, which is what it says.
      if (price.classList.contains('ba-coll__chip--clear')) {
        q.delete('filter.v.price.gte');
        q.delete('filter.v.price.lte');
      } else {
        if (lo) q.set('filter.v.price.gte', lo); else q.delete('filter.v.price.gte');
        if (hi) q.set('filter.v.price.lte', hi); else q.delete('filter.v.price.lte');
      }
      var qss = q.toString();
      history.pushState({}, '', base + (qss ? '?' + qss : ''));
      render(selected());
      return;
    }

    var chip = e.target.closest('[data-filter-tag]');
    if (!chip) return;
    if (chip.classList.contains('is-disabled')) { e.preventDefault(); return; }

    e.preventDefault();
    var tag = chip.getAttribute('data-filter-tag');
    var sel = selected();
    var i = sel.indexOf(tag);

    if (i !== -1) {
      sel.splice(i, 1);                 // ticking an applied value always just removes it
    } else {
      // One-of-N groups, marked data-single in coll-filter-group.liquid. Owner 2026-09-30:
      // "i should not be able to clikc both splashprof and waterproof it should be one fo the
      // other." Drop the group's other selections before adding this one, so the tick MOVES
      // rather than accumulating. Multi-select groups are untouched and still OR together.
      var group = groupOf(chip);
      if (group && group.getAttribute('data-single') === 'true') {
        var mine = tagsIn(group);
        sel = sel.filter(function (t) { return mine.indexOf(t) === -1; });
      }
      sel.push(tag);
    }

    push(sel);
    render(sel);
  });

  window.addEventListener('popstate', function () { render(selected()); });

  /* LANDING ON A SERVER-FILTERED PATH.

     Owner 2026-09-30: "when i click something on the collection it should not prmpt a mreload.
     it should simply filter what is being shown."

     The first version gave up whenever location.pathname was already a tag path, on the
     reasoning that the DOM then holds only a subset and cannot answer a wider question. That
     reasoning was sound and the behaviour was still wrong: the mega-menu chips link to tag
     paths, so arriving from the nav — or from any pre-existing link — put every shopper in the
     one state where the script switched itself off and every tick reloaded. A feature that works
     only if you enter by the front door is not working.

     Fixed by making the DOM able to answer: fetch the unfiltered collection once, swap in its
     grid, rewrite the URL to the query-string form, then filter normally. One request, only on
     that entry path, against collections of at most ~16 products. If the fetch fails we leave
     the page exactly as the server rendered it and the chips keep working as plain links. */
  if (location.pathname !== base) {
    var landed = pathTags();
    fetch(base, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
      .then(function (html) {
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.ba-coll__grid');
        if (!fresh) return;
        grid.innerHTML = fresh.innerHTML;
        // The index points at the tiles that were just replaced. Filtering against a stale
        // index would hide and show the wrong products, so it must be dropped here.
        invalidateIndex();
        var qs = new URLSearchParams(location.search);
        if (landed.length) qs.set('filter.p.tag', landed.join(','));
        var s = qs.toString();
        history.replaceState({}, '', base + (s ? '?' + s : ''));
        render(landed);
      })
      .catch(function () { /* server-rendered page stands; chips remain working links */ });
    return;
  }

  // Reproduce the selection a shared or reloaded URL carries.
  if (selected().length) render(selected());
  else disableDeadEnds([]);
})();
